import { useState, useRef, useEffect } from "react";
import {
    Form,
    Link,
    useNavigate,
    useRouteLoaderData,
    useSearchParams,
} from "react-router-dom";
import Cleave from "cleave.js/react";
import { states, cityStates } from "src/components/common/cityState";
import { Contact } from "./types";
import { AsyncFormSelect, SelectOption } from "src/components/common/AsyncSelect";
import { myAxios } from "../common/functions";
export default function ContactForm({
    keyword
}: {
    keyword: "Add" | "Edit";
}) {
    const loaderOrg =
    useRouteLoaderData("contact") ?? {
        organizationName: "", 
        organizationId: "", 
        firstName: "", 
        middleName: "",
        lastName: "",
        email1: "", 
        email2: "", 
        role: "", 
        directNumber: "",
        phoneNumber: "",
        extension: "",
        fax: "",
        street: "",
        city: "",
        state: "",
        zipCode: "",
        id: "",
        background: "",  
    };
    // emailValid
    const [formValues, setFormValues] = useState(loaderOrg);
    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormValues((prev:Contact)=> ({
            ...prev,
            [name]: value
        }));
    };
    const formRef = useRef(null);
    const organizationIdParam = useSearchParams()[0].get("organizationId");
    const navigate = useNavigate();
    const handleCopyBillingAddress = async () => {
        if(!formValues.organizationId){
            alert("No organization linked or organization not found.");
            return;
        }
        const formData = new FormData();
        formData.append("id", formValues.organizationId);
        const res = await myAxios({ fileName: "contacts/getOrganizationBillingAddress.php", formData });
        if(res && res.data){
            const { street, city, state, zipCode } = res.data;
            setFormValues((formValues:Contact) => ({...formValues, street, city, state, zipCode}));
        }
    }
    const handleCopyShippingAddress = async () => {
        if(!formValues.organizationId){
            alert("No organization linked or organization not found.");
            return;
        }
        const formData = new FormData();
        formData.append("id", formValues.organizationId);
        const res = await myAxios({ fileName: "contacts/getOrganizationShippingAddress.php", formData });
        if(res && res.data){
            const { shippingStreet: street, shippingCity: city, shippingState: state, shippingZipCode: zipCode } = res.data;
            setFormValues((formValues:Contact) => ({...formValues, street, city, state, zipCode}));
        }
    }
    const handleCopyOverseaBillingAddress = async () => {
        if(!formValues.organizationId){
            alert("No organization linked or organization not found.");
            return;
        }
        const formData = new FormData();
        formData.append("id", formValues.organizationId);
        const res = await myAxios({ fileName: "contacts/getOrganizationOverseaBillingAddress.php", formData });
        if(res && res.data){
            setFormValues((formValues:Contact) => ({...formValues, overseaAddress: res.data}));
        }
    }
    const handleCopyOverseaShippingAddress = async () => {
        if(!formValues.organizationId){
            alert("No organization linked or organization not found.");
            return;
        }
        const formData = new FormData();
        formData.append("id", formValues.organizationId);
        const res = await myAxios({ fileName: "contacts/getOrganizationOverseaShippingAddress.php", formData });
        if(res && res.data){
            setFormValues((formValues:Contact) => ({...formValues, overseaAddress: res.data}));
        }
    }
    useEffect(() => {
        const controller = new AbortController();
        let isMouted = true;
        const getData = async () => {
            if(keyword === "Add" && organizationIdParam){
                const formData = new FormData();
                formData.append("id", organizationIdParam ?? "");
                const res = await myAxios({ fileName: "contacts/getOrganizationName.php", formData, signal: controller.signal });
                if(!isMouted){
                    return;
                }
                if(!res || !res.data){
                     navigate(`/Contacts/Add`, { replace: true });
                     return;
                }
                setFormValues((formValues:Contact) => ({...formValues, organizationName: res.data.name, organizationId: organizationIdParam}));
            }
        }
        getData();
        return () => {
            isMouted = false;
            controller.abort();
        };
    }, [keyword, organizationIdParam, navigate]);
    return (
        <Form method="post" ref = {formRef}>
            <input type="hidden" name="action" value={keyword} />
            <input type="hidden" name="id" value={formValues.id ?? ""} />
            <div className="cssSeperateWhenWide ps-3 mb-2">
                <div className="flex-grow-1 me-3">
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="organizationId">
                            Organization Name:
                        </label>
                        <AsyncFormSelect
                            className="flex-grow-1 ms-3"
                            placeholder = "Search organization…"
                            // required = {!!field.is_required} --- IGNORE ---
                            name = {"organizationId"}
                            inputId = {"organizationId"}
                            readOnly = {!!organizationIdParam}
                            fileName = "common/searchOrganization.php"
                            value = {formValues.organizationId ? {label: formValues.organizationName, value: formValues.organizationId} : null}
                            onChange = {next => 
                                setFormValues({
                                    ...formValues,
                                    organizationId: (next as SelectOption)?.value ?? null,
                                    organizationName: (next as SelectOption)?.label ?? null
                                })
                            }
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap required-field" htmlFor="firstName">
                            First Name:
                        </label>
                        <input
                            required
                            className="form-control flex-grow-1 ms-3"
                            id="firstName"
                            name="firstName"
                            value={formValues.firstName}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="middleName">
                            Middle Name:
                        </label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="middleName"
                            name="middleName"
                            value={formValues.middleName}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="lastName">
                            Last Name:
                        </label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="lastName"
                            name="lastName"
                            value={formValues.lastName}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap required-field" htmlFor="email1">
                            Email 1:
                        </label>
                        <input
                            required
                            className="form-control flex-grow-1 ms-3"
                            id = "email1"
                            name="email1"
                            value={formValues.email1}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="email2">
                            Email 2:
                        </label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="email2"
                            name="email2"
                            value={formValues.email2}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold" htmlFor="role">Role:</label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="role"
                            name="role"
                            value={formValues.role}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="directNumber">Direct Number:</label>
                        <Cleave
                            className="form-control flex-grow-1 ms-3"
                            id="directNumber"
                            name="directNumber"
                            value={formValues.directNumber}
                            onChange={(e) =>
                                setFormValues({
                                    ...formValues,
                                    directNumber: e.target.value
                                })
                            }
                            options={{
                                blocks: [3, 3, 4],
                                delimiter: "-",
                                numericOnly: true
                            }}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="phoneNumber">Business Phone:</label>
                        <Cleave
                            className="form-control flex-grow-1 ms-3"
                            id="phoneNumber"
                            name="phoneNumber"
                            value={formValues.phoneNumber}
                            onChange={(e) =>
                                setFormValues({
                                    ...formValues,
                                    phoneNumber: e.target.value
                                })
                            }
                            options={{
                                blocks: [3, 3, 4],
                                delimiter: "-",
                                numericOnly: true
                            }}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold" htmlFor="extension">Extension:</label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="extension"
                            name="extension"
                            value={formValues.extension}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold" htmlFor="fax">Fax:</label>
                        <Cleave
                            className="form-control flex-grow-1 ms-3"
                            id="fax"
                            name="fax"
                            value={formValues.fax}
                            onChange={(e) =>
                                setFormValues({
                                    ...formValues,
                                    fax: e.target.value
                                })
                            }
                            options={{
                                blocks: [3, 3, 4],
                                delimiter: "-",
                                numericOnly: true
                            }}
                        />
                    </div>
                </div>
                <div className="flex-grow-1 me-3">
                    <h4 className = "fw-bold text-center mt-2">Address</h4>
                    <button type="button" className="btn btn-warning" onClick={handleCopyBillingAddress}>Copy Billing Address from Organization</button>
                    <button type="button" className="btn btn-info ms-2" onClick={handleCopyShippingAddress}>Copy Shipping Address from Organization</button>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold" htmlFor="street">Street:</label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="street"
                            name="street"
                            value={formValues.street}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold" htmlFor="city">City:</label>
                        <select
                            className="form-control flex-grow-1 ms-3"
                            id="city"
                            name="city"
                            value={formValues.city}
                            onChange={handleChange}
                        >
                            <option value="">Select</option>
                            {formValues.state &&
                                cityStates[formValues.state as keyof typeof cityStates]?.map(
                                    (s, i) => <option key={i}>{s}</option>
                                )}
                        </select>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold" htmlFor="state">State:</label>
                        <select
                            className = "form-control flex-grow-1 ms-3"
                            id="state"
                            name = "state"
                            value = {formValues.state}
                            onChange = {handleChange}
                        >
                            <option value="">Select</option>
                            {states.map((s, i) => (
                                <option key={i}>{s}</option>
                            ))}
                        </select>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="zipCode">Zip Code:</label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="zipCode"
                            name="zipCode"
                            value={formValues.zipCode}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="mt-2">
                        <button type="button" className="btn btn-warning" onClick={handleCopyOverseaBillingAddress}>Copy Oversea Billing Address from Organization</button>
                        <button type="button" className="btn btn-info ms-2" onClick={handleCopyOverseaShippingAddress}>Copy Oversea Shipping Address from Organization</button>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="overseaAddress">Oversea Address:</label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="overseaAddress"
                            name="overseaAddress"
                            value={formValues.overseaAddress}
                            onChange={handleChange}
                        />
                    </div>
                </div>
                <div className="d-flex flex-column align-items-start mt-2">
                    <label className="fw-bold" htmlFor="background">Background:</label>
                    <textarea
                        className="form-control w-100 flex-grow-1"
                        id="background"
                        name="background"
                        value={formValues.background}
                        onChange={handleChange}
                    />
                </div>
            </div>
            <div className="my-3">
                <button
                    type="submit"
                    className="btn btn-primary"
                >
                    {keyword}
                </button>
                <Link
                    to={organizationIdParam?`/Organizations/${organizationIdParam}`:".."}
                    className="btn btn-outline-secondary float-end"
                >
                    Back
                </Link>
            </div>
        </Form>
    );
}
